import React, { useContext, useState } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "../../UserContext";
import { useTranslation } from "react-i18next";
import { IoMenuSharp } from "react-icons/io5";
import { FaCog, FaBuilding, FaThLarge, FaBug, FaUsers, FaTasks, FaTools, FaDownload } from "react-icons/fa";
import { FiBell } from "react-icons/fi";

// CSS and Asset Imports
import "./styles.css";
import sidebarImage from "../../assets/Sidebar/sidebar.PNG";
import farm from "../../assets/Sidebar/farm.png";
import financials from "../../assets/Sidebar/financials.png";
import coloredFinancials from "../../assets/Sidebar/colored_financials.png";
import management from "../../assets/Sidebar/management.png";
import coloredManagement from "../../assets/Sidebar/colored_management.png";
import payments from "../../assets/Sidebar/payments.png";
import coloredPayments from "../../assets/Sidebar/colored_payments.png";
import invoices from "../../assets/Sidebar/invoices.png";
import coloredInvoices from "../../assets/Sidebar/colored_invoices.png";
import employees from "../../assets/Sidebar/employees.png";
import coloredEmployees from "../../assets/Sidebar/colored_employees.png";
import InventoryProducts from "../../assets/Sidebar/Inventory&Product.png";
import coloredInventoryProducts from "../../assets/Sidebar/Inventory&products_colored.png";
import products from "../../assets/Sidebar/Product.png";
import coloredProducts from "../../assets/Sidebar/product_colored.png";

const CustomSidebar = () => {
  const [t] = useTranslation();
  const { userData } = useContext(UserContext);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const handleSidebarCollapse = () => {
    setCollapsed(!collapsed);
  };

  // Comprehensive active state logic from both versions
  const isDashboardActive = location.pathname === "/dashboard";
  const isProfileActive = location.pathname === "/profile";
  const isSettingsActive = location.pathname === "/settings";
  // const isExportActive = location.pathname === "/export"; // Disabled - export feature hidden
  const isNotificationsActive = location.pathname === "/notifications";

  const isPaymentsActive = location.pathname === "/payments";
  const isInvoicesActive = location.pathname === "/invoices";
  const isFinancialsActive = isPaymentsActive || isInvoicesActive;

  const isFieldsActive = location.pathname === "/fields";
  const isTasksActive = location.pathname === "/tasks" || location.pathname.startsWith("/tasks/");
  const isProblemsActive = location.pathname.startsWith("/problems");
  const isPlantingManagementActive = isFieldsActive || isTasksActive || isProblemsActive;

  const isEmployeesActive = location.pathname === "/employees";
  const isBusinessEntitiesActive = location.pathname.startsWith("/business-entities");
  const isEmployeesEntitiesActive = isEmployeesActive || isBusinessEntitiesActive;
  const isInventoryActive = location.pathname.startsWith("/inventory");
  const isProductsActive = location.pathname === "/products";
  const isInventoryProductsActive = isInventoryActive || isProductsActive;
  const isAssetsActive = location.pathname.startsWith("/assets");

  return (
    <Sidebar
      className="sidebar"
      collapsed={collapsed}
      width="236px"
      collapsedWidth="72px"
    >
      <Menu iconShape="square">
        {/* --- Header & Collapse Button --- */}
        <MenuItem
          style={{
            display: "flex",
            backgroundImage: `url(${sidebarImage})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            height: "5em",
            marginBottom: "2em",
          }}
          onClick={handleSidebarCollapse}
        >
          <IoMenuSharp
            size={25}
            style={{
              marginLeft: collapsed ? 0 : "11em",
            }}
          />
        </MenuItem>

        {/* --- Farm / Dashboard Link --- */}
        <div className="farmName">
          <MenuItem
            component={<Link to="/dashboard" className="links" />}
            icon={
              <img src={farm} alt="Farm" style={{ width: 24, height: 24 }} />
            }
            style={{
              backgroundColor: isDashboardActive ? "#EDCFD9" : "transparent",
              color: isDashboardActive ? "#A50F41" : "#372068",
              borderRadius: isDashboardActive ? "8px" : "0px",
            }}
          >
            {userData?.Farm.name}
          </MenuItem>
        </div>

        {/* --- Financials SubMenu --- */}
        <SubMenu
          icon={
            <img
              src={isFinancialsActive ? coloredFinancials : financials}
              alt="Financials"
            />
          }
          label={t("Financials")}
          defaultOpen={isFinancialsActive}
          style={{
            color: isFinancialsActive ? "#A50F41" : "#372068",
          }}
        >
          <MenuItem
            component={<Link to="/payments" className="links" />}
            icon={
              <img
                src={isPaymentsActive ? coloredPayments : payments}
                alt="Expenses & Sales"
                className="items"
              />
            }
            style={{
              backgroundColor: isPaymentsActive ? "#EDCFD9" : "transparent",
              color: isPaymentsActive ? "#A50F41" : "#372068",
              borderRadius: isPaymentsActive ? "8px" : "0px",
              paddingRight: "30px",
              paddingLeft: "30px",
            }}
          >
            {t("Expenses & Sales")}
          </MenuItem>
          <MenuItem
            component={<Link to="/invoices" className="links" />}
            icon={
              <img
                src={isInvoicesActive ? coloredInvoices : invoices}
                alt="Invoices"
                className="items"
              />
            }
            style={{
              backgroundColor: isInvoicesActive ? "#EDCFD9" : "transparent",
              color: isInvoicesActive ? "#A50F41" : "#372068",
              borderRadius: isInvoicesActive ? "8px" : "0px",
              paddingRight: "30px",
              paddingLeft: "30px",
            }}
          >
            {t("Invoices")}
          </MenuItem>
        </SubMenu>

        {/* --- Planting Management SubMenu --- */}
        <SubMenu
          icon={
            <img
              src={isPlantingManagementActive ? coloredManagement : management}
              alt="Planting Management"
            />
          }
          label={t("Planting Management")}
          defaultOpen={isPlantingManagementActive}
          style={{
            color: isPlantingManagementActive ? "#A50F41" : "#372068",
          }}
        >
          <MenuItem
            component={<Link to="/fields" className="links" />}
            icon={
              <FaThLarge
                size={20}
                color={isFieldsActive ? "#A50F41" : "#372068"}
              />
            }
            style={{
              backgroundColor: isFieldsActive ? "#EDCFD9" : "transparent",
              color: isFieldsActive ? "#A50F41" : "#372068",
              borderRadius: isFieldsActive ? "8px" : "0px",
              paddingRight: "30px",
              paddingLeft: "30px",
            }}
          >
            {t("Fields")}
          </MenuItem>
          <MenuItem
            component={<Link to="/tasks" className="links" />}
            icon={
              <FaTasks
                size={20}
                color={isTasksActive ? "#A50F41" : "#372068"}
              />
            }
            style={{
              backgroundColor: isTasksActive ? "#EDCFD9" : "transparent",
              color: isTasksActive ? "#A50F41" : "#372068",
              borderRadius: isTasksActive ? "8px" : "0px",
              paddingRight: "30px",
              paddingLeft: "30px",
            }}
          >
            {t("Tasks")}
          </MenuItem>
          <MenuItem
            component={<Link to="/problems" className="links" />}
            icon={
              <FaBug
                size={20}
                color={isProblemsActive ? "#A50F41" : "#372068"}
              />
            }
            style={{
              backgroundColor: isProblemsActive ? "#EDCFD9" : "transparent",
              color: isProblemsActive ? "#A50F41" : "#372068",
              borderRadius: isProblemsActive ? "8px" : "0px",
              paddingRight: "30px",
              paddingLeft: "30px",
            }}
          >
            {t("Problems")}
          </MenuItem>
        </SubMenu>

        {/* --- Inventory and Products SubMenu --- */}
        <SubMenu
          icon={
            <img
              src={
                isInventoryProductsActive
                  ? coloredInventoryProducts
                  : InventoryProducts
              }
              alt="Inventory and Products"
            />
          }
          label={t("Inventory and Products")}
          defaultOpen={isInventoryProductsActive}
          style={{
            color: isInventoryProductsActive ? "#A50F41" : "#372068",
          }}
        >
          <MenuItem
            component={<Link to="/inventory" className="links" />}
            icon={
              <FaThLarge
                size={20}
                color={isInventoryActive ? "#A50F41" : "#372068"}
              />
            }
            style={{
              backgroundColor: isInventoryActive ? "#EDCFD9" : "transparent",
              color: isInventoryActive ? "#A50F41" : "#372068",
              borderRadius: isInventoryActive ? "8px" : "0px",
              paddingRight: "30px",
              paddingLeft: "30px",
            }}
          >
            {t("Inventory")}
          </MenuItem>
          <MenuItem
            component={<Link to="/products" className="links" />}
            icon={
              <img
                src={isProductsActive ? coloredProducts : products}
                alt="Products"
                className="items"
              />
            }
            style={{
              backgroundColor: isProductsActive ? "#EDCFD9" : "transparent",
              color: isProductsActive ? "#A50F41" : "#372068",
              borderRadius: isProductsActive ? "8px" : "0px",
              paddingRight: "30px",
              paddingLeft: "30px",
            }}
          >
            {t("Products")}
          </MenuItem>
        </SubMenu>

        {/* --- Employees & Entities SubMenu --- */}
        <SubMenu
          icon={
            <img
              src={isEmployeesEntitiesActive ? coloredEmployees : employees}
              alt="Employees & Entities"
            />
          }
          label={t("Employees & Entities")}
          defaultOpen={isEmployeesEntitiesActive}
          style={{
            color: isEmployeesEntitiesActive ? "#A50F41" : "#372068",
          }}
        >
          <MenuItem
            component={<Link to="/employees" className="links" />}
            icon={
              <img
                src={isEmployeesActive ? coloredEmployees : employees}
                alt="Employees"
                className="items"
              />
            }
            style={{
              backgroundColor: isEmployeesActive ? "#EDCFD9" : "transparent",
              color: isEmployeesActive ? "#A50F41" : "#372068",
              borderRadius: isEmployeesActive ? "8px" : "0px",
              paddingRight: "30px",
              paddingLeft: "30px",
            }}
          >
            {t("Employees")}
          </MenuItem>
          <MenuItem
            component={<Link to="/business-entities" className="links" />}
            icon={
              <FaUsers
                size={20}
                color={isBusinessEntitiesActive ? "#A50F41" : "#372068"}
              />
            }
            style={{
              backgroundColor: isBusinessEntitiesActive ? "#EDCFD9" : "transparent",
              color: isBusinessEntitiesActive ? "#A50F41" : "#372068",
              borderRadius: isBusinessEntitiesActive ? "8px" : "0px",
              paddingRight: "30px",
              paddingLeft: "30px",
            }}
          >
            {t("Suppliers & Clients")}
          </MenuItem>
        </SubMenu>

        {/* --- Assets Management --- */}
        <MenuItem
          component={<Link to="/assets" className="links" />}
          icon={
            <FaTools
              size={20}
              color={isAssetsActive ? "#A50F41" : "#372068"}
            />
          }
          style={{
            backgroundColor: isAssetsActive ? "#EDCFD9" : "transparent",
            color: isAssetsActive ? "#A50F41" : "#372068",
            borderRadius: isAssetsActive ? "8px" : "0px",
          }}
        >
          {t("Assets Management")}
        </MenuItem>

        {/* --- Standalone Menu Items --- */}
        <MenuItem
          component={<Link to="/profile" className="links" />}
          icon={
            <FaBuilding
              size={23}
              color={isProfileActive ? "#A50F41" : "#372068"}
            />
          }
          style={{
            backgroundColor: isProfileActive ? "#EDCFD9" : "transparent",
            color: isProfileActive ? "#A50F41" : "#372068",
            borderRadius: isProfileActive ? "8px" : "0px",
          }}
        >
          {t("Company Profile")}
        </MenuItem>

        <MenuItem
          component={<Link to="/notifications" className="links" />}
          icon={
            <FiBell size={23} color={isNotificationsActive ? "#A50F41" : "#372068"} />
          }
          style={{
            backgroundColor: isNotificationsActive ? "#EDCFD9" : "transparent",
            color: isNotificationsActive ? "#A50F41" : "#372068",
            borderRadius: isNotificationsActive ? "8px" : "0px",
          }}
        >
          {t("notifications.title")}
        </MenuItem>

        <MenuItem
          component={<Link to="/settings" className="links" />}
          icon={
            <FaCog size={23} color={isSettingsActive ? "#A50F41" : "#372068"} />
          }
          style={{
            backgroundColor: isSettingsActive ? "#EDCFD9" : "transparent",
            color: isSettingsActive ? "#A50F41" : "#372068",
            borderRadius: isSettingsActive ? "8px" : "0px",
          }}
        >
          {t("Settings")}
        </MenuItem>

        {/* Export Data feature disabled - hidden from all users */}
        {/* {userData?.Role?.id === 1 && (
          <MenuItem
            component={<Link to="/export" className="links" />}
            icon={
              <FaDownload size={23} color={isExportActive ? "#A50F41" : "#372068"} />
            }
            style={{
              backgroundColor: isExportActive ? "#EDCFD9" : "transparent",
              color: isExportActive ? "#A50F41" : "#372068",
              borderRadius: isExportActive ? "8px" : "0px",
            }}
          >
            {t("Export Data")}
          </MenuItem>
        )} */}
      </Menu>
    </Sidebar>
  );
};

export default CustomSidebar;
